import React, { useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import Select from 'react-select'
import { currencyFormat } from 'resources/helper'
import collapseIcon from '../../assets/images/collapse-alt-svgrepo-com-w.svg'
import expandIcon from '../../assets/images/expand-svgrepo-com-w.svg'
import DotLoader from '../../components/DotLoader'
import ScrollToggle from '../../components/ScrollToggle/ScrollToggle'
import { API_URLS, MONTHS } from '../../resources/constants'
import { fileDownloadBlob, lastYears } from '../../resources/functions'
import { CallApi } from '../../saga/callApi'
import { getBranch } from '../../store/user/selectors'
import './BudgetVsActual.css'
import { Button } from '@mui/material'

interface MonthData {
  month_label: string
  actual: number
  budget: number
  over_budget: number
  pct_of_budget: number
}

interface BvaRow {
  account_chart: {
    id: number
    account: number
    name: string
    report_code: string
  }
  months: MonthData[]
}

const HEADER_CODES = ['GH', 'SH', 'SSH']

// Indent per hierarchy level (px)

const INDENT: Record<string, number> = {
  GH: 12,
  GT: 12,
  SH: 26,
  ST: 26,
  SSH: 40,
  SST: 40,
}

const DEFAULT_INDENT = 48
const COL = { label: 300, actual: 125, budget: 145, over: 165, pct: 115 }
const selectStyles = {
  control: (base: any) => ({
    ...base,
    minHeight: 36,
    borderColor: '#e4e7ec',
    boxShadow: 'none',
  }),

  menu: (base: any) => ({ ...base, zIndex: 20 }),
}

const BudgetVsActual: React.FC = () => {
  const { t } = useTranslation('scenes.BudgetVsActual.index')
  const branchData = useSelector(getBranch)
  const branchId = +branchData['id']
  const currentYear = new Date().getFullYear()
  const filterTypeOptions = useMemo(
    () => [
      {
        value: 'quarter',
        label: t('filter.type.quarter', { defaultValue: 'Quarter' }),
      },
      {
        value: 'year',
        label: t('filter.type.year', { defaultValue: 'Year' }),
      },
      {
        value: 'custom',
        label: t('filter.type.custom', { defaultValue: 'Custom' }),
      },
    ],
    [t]
  )

  const quarterOptions = useMemo(
    () => [
      {
        value: 'ALL',
        label: t('quarter.allQuarters', {
          defaultValue: 'All Quarters (Q1 - Q4)',
        }),
      },
      {
        value: 'Q1',
        label: t('quarter.q1', { defaultValue: 'Q1 (Jan - Mar)' }),
        from: '01',
        to: '03',
      },
      {
        value: 'Q2',
        label: t('quarter.q2', { defaultValue: 'Q2 (Apr - Jun)' }),
        from: '04',
        to: '06',
      },
      {
        value: 'Q3',
        label: t('quarter.q3', { defaultValue: 'Q3 (Jul - Sep)' }),
        from: '07',
        to: '09',
      },
      {
        value: 'Q4',
        label: t('quarter.q4', { defaultValue: 'Q4 (Oct - Dec)' }),
        from: '10',
        to: '12',
      },
    ],
    [t]
  )

  const [searchParams, setSearchParams] = useState<any>(() => ({
    year: { value: currentYear, label: String(currentYear) },
    filter_type: {
      value: 'quarter',
      label: t('filter.type.quarter', { defaultValue: 'Quarter' }),
    },
    quarter: {
      value: 'ALL',
      label: t('quarter.allQuarters', {
        defaultValue: 'All Quarters (Q1 - Q4)',
      }),
    },
    from_month: {
      value: '01',
      label: t('month.01', { ns: 'resources.form-validator' }),
    },
    to_month: {
      value: '12',
      label: t('month.12', { ns: 'resources.form-validator' }),
    },
  }))
  const [reportData, setReportData] = useState<BvaRow[]>([])
  const [monthLabels, setMonthLabels] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasData, setHasData] = useState(false)
  const [collapsedKeys, setCollapsedKeys] = useState<Record<string, boolean>>(
    {}
  )
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const formatAmount = (value: number) => {
    const fixed = Math.abs(value).toFixed(2)
    return value < 0 ? `(${currencyFormat(fixed)})` : currencyFormat(fixed)
  }
  const formatPct = (value: number) => `${value.toFixed(2)}%`
  const varianceClass = (value: number) =>
    value > 0 ? 'bva-over' : value < 0 ? 'bva-under' : 'bva-zero'

  // Associate each row with its active parent header group
  const processedRows = useMemo(() => {
    let currentGhKey = ''
    let currentShKey = ''
    let currentSshKey = ''
    return reportData.map((row, idx) => {
      const code = row.account_chart.report_code
      const rowKey = `${code}_${row.account_chart.id || idx}`
      if (code === 'GH') {
        currentGhKey = rowKey
        currentShKey = ''
        currentSshKey = ''
      } else if (code === 'SH') {
        currentShKey = rowKey
        currentSshKey = ''
      } else if (code === 'SSH') {
        currentSshKey = rowKey
      }
      const parentKeys: string[] = []
      if (code !== 'GH' && currentGhKey) parentKeys.push(currentGhKey)
      if (!['GH', 'SH'].includes(code) && currentShKey)
        parentKeys.push(currentShKey)
      if (!['GH', 'SH', 'SSH'].includes(code) && currentSshKey)
        parentKeys.push(currentSshKey)
      return {
        ...row,
        rowKey,
        isHeader: HEADER_CODES.includes(code),
        parentKeys,
      }
    })
  }, [reportData])

  const headerRows = useMemo(
    () => processedRows.filter((r) => r.isHeader),
    [processedRows]
  )

  const allGhOpen = useMemo(() => {
    if (headerRows.length === 0) return false
    return headerRows.every((r) => !collapsedKeys[r.rowKey])
  }, [headerRows, collapsedKeys])

  const toggleRow = (key: string) => {
    setCollapsedKeys((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const toggleAll = (collapse?: boolean) => {
    const shouldCollapse = collapse !== undefined ? collapse : allGhOpen
    const updated: Record<string, boolean> = {}

    processedRows.forEach((r) => {
      if (r.isHeader) updated[r.rowKey] = shouldCollapse
    })

    setCollapsedKeys(updated)
  }

  const buildQuery = () => {
    const year = searchParams.year.value
    const filterType = searchParams.filter_type?.value || 'year'
    let fromMonth = 1
    let toMonth = 12
    let quarter: string | undefined = undefined
    let viewBy = 'month'

    if (filterType === 'year') {
      fromMonth = 1
      toMonth = 12
      quarter = 'FY'
      viewBy = 'year'
    } else if (filterType === 'quarter') {
      viewBy = 'quarter'
      const q = searchParams.quarter || quarterOptions[0]
      if (q.value === 'ALL') {
        fromMonth = 1
        toMonth = 12
        quarter = 'ALL'
      } else {
        fromMonth = +q.from
        toMonth = +q.to
        quarter = q.value
      }
    } else {
      viewBy = 'month'
      fromMonth = +searchParams.from_month.value
      toMonth = +searchParams.to_month.value
    }

    const queryParams: Record<string, string> = {
      year: String(year),
      from_month: String(fromMonth),
      to_month: String(toMonth),
      branch_id: String(branchId),
      view_by: viewBy,
    }
    if (quarter) {
      queryParams.quarter = quarter
    }
    const qs = new URLSearchParams(queryParams).toString()
    return { qs, year, fromMonth, toMonth, quarter, filterType, viewBy }
  }

  const generateReport = () => {
    const { qs, fromMonth, toMonth, filterType } = buildQuery()
    if (filterType === 'custom' && fromMonth > toMonth) {
      toast.error(t('msg.invalidMonthRange'))
      return
    }
    setIsLoading(true)
    setHasData(false)
    setReportData([])
    setCollapsedKeys({})
    CallApi.GET(`${API_URLS.BUDGET.BUDGET_VS_ACTUAL}?${qs}`, {}, true)
      .then((resp: any) => {
        const data = resp?.data?.data
        if (data && data.result && data.result.length > 0) {
          setReportData(data.result)
          setMonthLabels(data.month_labels ?? [])
          setHasData(true)
        } else {
          setReportData([])
          setMonthLabels([])
          setHasData(false)
          toast.error(t('msg.noData'))
        }
      })
      .catch((err: any) => {
        const message =
          err?.data?.message || err?.response?.data?.message || t('msg.error')
        toast.error(message)
      })
      .finally(() => setIsLoading(false))
  }

  const downloadPdf = () => {
    const { qs, year, fromMonth, toMonth, quarter, filterType, viewBy } =
      buildQuery()
    setIsLoading(true)
    CallApi.DOWNLOAD(
      `${API_URLS.BUDGET.BUDGET_VS_ACTUAL_REPORT}?${qs}`,
      {},
      true
    )
      .then((resp: any) => {
        let fileSuffix = 'all-quarters'
        if (filterType === 'year') {
          fileSuffix = `fy-${year}`
        } else if (filterType === 'quarter') {
          fileSuffix =
            quarter && quarter !== 'ALL'
              ? quarter.toLowerCase()
              : 'all-quarters'
        } else if (filterType === 'custom') {
          fileSuffix = `${String(fromMonth).padStart(2, '0')}-${String(
            toMonth
          ).padStart(2, '0')}`
        }
        fileDownloadBlob(resp, `budget-vs-actual-${year}-${fileSuffix}`)
      })
      .catch((err: any) => {
        const message = err?.response?.data?.message || t('msg.downloadError')
        toast.error(message)
      })
      .finally(() => setIsLoading(false))
  }
  const colSpanCount = monthLabels.length * 4
  const isSingleColGroup = monthLabels.length <= 1
  const tableWidth = isSingleColGroup
    ? '100%'
    : COL.label +
      monthLabels.length * (COL.actual + COL.budget + COL.over + COL.pct)
  const renderPct = (value: number) => {
    const isOver = value > 100
    return (
      <div className="bva-pct">
        <span
          className={`bva-bar ${isOver ? 'is-over' : ''}`}
          aria-hidden="true"
        >
          <i style={{ width: `${Math.max(0, Math.min(value, 100))}%` }} />
        </span>
        <span className={isOver ? 'bva-pct-over' : undefined}>
          {formatPct(value)}
        </span>
      </div>
    )
  }

  return (
    <div className="main-container flex-grow-1">
      <div className="container-fluid bva">
        {/* Toolbar */}

        <div className="bva-toolbar align-items-center">
          <div>
            <h1 className="bva-title">{t('page.title')}</h1>

            {hasData && (
              <p className="bva-subtitle">
                {searchParams.year.label}
                {searchParams.filter_type?.value === 'year'
                  ? ` · FY ${searchParams.year.value}`
                  : searchParams.filter_type?.value === 'quarter'
                  ? ` · ${
                      searchParams.quarter?.label ||
                      t('quarter.allQuarters', {
                        defaultValue: 'All Quarters (Q1 - Q4)',
                      })
                    }`
                  : ` · ${searchParams.from_month.label} – ${searchParams.to_month.label}`}
              </p>
            )}
          </div>

          <div className="bva-controls">
            <div className="bva-field">
              <label>{t('filter.year')}</label>

              <Select
                styles={selectStyles}
                onChange={(e: any) =>
                  setSearchParams({ ...searchParams, year: e })
                }
                options={lastYears(5)}
                value={searchParams.year}
              />
            </div>

            <div className="bva-field bva-field-filter-type">
              <label>{t('filter.filterBy')}</label>

              <Select
                styles={selectStyles}
                onChange={(e: any) =>
                  setSearchParams({ ...searchParams, filter_type: e })
                }
                options={filterTypeOptions}
                value={searchParams.filter_type}
              />
            </div>

            {searchParams.filter_type?.value === 'quarter' && (
              <div className="bva-field bva-field-quarter">
                <label>{t('filter.quarter')}</label>

                <Select
                  styles={selectStyles}
                  onChange={(e: any) =>
                    setSearchParams({ ...searchParams, quarter: e })
                  }
                  options={quarterOptions}
                  value={searchParams.quarter}
                />
              </div>
            )}

            {searchParams.filter_type?.value === 'custom' && (
              <>
                <div className="bva-field">
                  <label>{t('filter.fromMonth')}</label>

                  <Select
                    styles={selectStyles}
                    onChange={(e: any) =>
                      setSearchParams({ ...searchParams, from_month: e })
                    }
                    options={MONTHS}
                    value={searchParams.from_month}
                  />
                </div>

                <div className="bva-field">
                  <label>{t('filter.toMonth')}</label>

                  <Select
                    styles={selectStyles}
                    onChange={(e: any) =>
                      setSearchParams({ ...searchParams, to_month: e })
                    }
                    options={MONTHS}
                    value={searchParams.to_month}
                  />
                </div>
              </>
            )}
            <Button
              onClick={generateReport}
              variant="contained"
              startIcon={<i className="fa fa-search" aria-hidden="true" />}
              disabled={isLoading}
              sx={{
                backgroundColor: '#0d6efd',
                '&:hover': {
                  backgroundColor: '#0b5ed7',
                },
                textTransform: 'none',
                fontSize: '14px',
              }}
            >
              {t('filter.generate')}
            </Button>
            {hasData && (
              <Button
                onClick={downloadPdf}
                variant="contained"
                startIcon={
                  <i className="fa fa-download " aria-hidden="true" />
                }
                disabled={isLoading}
                sx={{
                  backgroundColor: '#000000',
                  '&:hover': {
                    backgroundColor: '#000000',
                  },
                  textTransform: 'none',
                  fontSize: '14px',
                }}
              >
                {t('filter.exportPdf')}
              </Button>
            )}
            <div className="filter-btn">
              <div className={`page-loader ${isLoading ? 'visible' : ''}`}>
                <div className="loader"></div>
              </div>

              {isLoading && <DotLoader text={t('filter.loading')} />}
            </div>
          </div>
        </div>

        {/* Report */}

        <div className="executive-dashboard p-0">
          {!hasData ? (
            <div className="bva-empty">{t('table.notAvailable')}</div>
          ) : (
            <>
              <div className="bva-shell" ref={tableContainerRef}>
                <table
                  className="bva-table"
                  style={{
                    width: tableWidth,
                    minWidth: isSingleColGroup ? '100%' : undefined,
                  }}
                >
                  <colgroup>
                    <col
                      style={{
                        width: isSingleColGroup ? '30%' : COL.label,
                        minWidth: COL.label,
                      }}
                    />

                    {monthLabels.map((_, i) => (
                      <React.Fragment key={i}>
                        <col
                          style={{
                            width: isSingleColGroup ? '16%' : COL.actual,
                            minWidth: COL.actual,
                          }}
                        />

                        <col
                          style={{
                            width: isSingleColGroup ? '18%' : COL.budget,
                            minWidth: COL.budget,
                          }}
                        />

                        <col
                          style={{
                            width: isSingleColGroup ? '21%' : COL.over,
                            minWidth: COL.over,
                          }}
                        />

                        <col
                          style={{
                            width: isSingleColGroup ? '15%' : COL.pct,
                            minWidth: COL.pct,
                          }}
                        />
                      </React.Fragment>
                    ))}
                  </colgroup>

                  <thead>
                    <tr className="bva-head-top">
                      <th className="bva-corner" rowSpan={2}>
                        <div className="bva-corner-inner">
                          <button
                            type="button"
                            className="bva-corner-toggle-btn"
                            onClick={() => toggleAll()}
                            title={
                              allGhOpen
                                ? t('label.collapseAll', {
                                    defaultValue: 'Collapse All',
                                  })
                                : t('label.expandAll', {
                                    defaultValue: 'Expand All',
                                  })
                            }
                            aria-label={
                              allGhOpen
                                ? t('label.collapseAll', {
                                    defaultValue: 'Collapse All',
                                  })
                                : t('label.expandAll', {
                                    defaultValue: 'Expand All',
                                  })
                            }
                          >
                            <img
                              src={allGhOpen ? collapseIcon : expandIcon}
                              alt=""
                              style={{ height: '20px', width: '20px' }}
                            />
                          </button>
                          <span>{t('table.financialRow')}</span>
                        </div>
                      </th>

                      {monthLabels.map((label, i) => (
                        <th
                          key={i}
                          colSpan={4}
                          className={`text-center ${
                            i > 0 ? 'bva-month-start' : ''
                          } ${label === 'Total' ? 'bva-total-col' : ''}`}
                        >
                          {label}
                        </th>
                      ))}
                    </tr>

                    <tr className="bva-head-sub">
                      {monthLabels.map((label, i) => (
                        <React.Fragment key={i}>
                          <th
                            className={`bva-month-start ${
                              label === 'Total' ? 'bva-total-col' : ''
                            }`}
                          >
                            {t('table.amount')}
                          </th>

                          <th
                            className={label === 'Total' ? 'bva-total-col' : ''}
                            title={t('table.budgetAmount')}
                          >
                            {t('table.budgetAmount')}
                          </th>

                          <th
                            className={label === 'Total' ? 'bva-total-col' : ''}
                            title={t('table.overBudget')}
                          >
                            {t('table.overBudget')}
                          </th>

                          <th
                            className={label === 'Total' ? 'bva-total-col' : ''}
                            title={t('table.pctOfBudget')}
                          >
                            {t('table.pctOfBudget')}
                          </th>
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {processedRows.map((row) => {
                      const { report_code } = row.account_chart

                      const isCollapsed = !!collapsedKeys[row.rowKey]

                      const isHidden = row.parentKeys.some(
                        (k) => collapsedKeys[k]
                      )

                      if (isHidden) return null

                      const indent = INDENT[report_code] ?? DEFAULT_INDENT

                      return (
                        <tr key={row.rowKey} className={`bva-${report_code}`}>
                          <td
                            className={`bva-label ${
                              row.isHeader ? 'bva-row-header' : ''
                            }`}
                            style={{ paddingLeft: indent }}
                            title={row.account_chart.name}
                            onClick={() =>
                              row.isHeader && toggleRow(row.rowKey)
                            }
                            {...(row.isHeader
                              ? {
                                  role: 'button',

                                  tabIndex: 0,

                                  'aria-expanded': !isCollapsed,

                                  onKeyDown: (e: React.KeyboardEvent) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault()

                                      toggleRow(row.rowKey)
                                    }
                                  },
                                }
                              : {})}
                          >
                            {row.isHeader && (
                              <i
                                className={
                                  !isCollapsed
                                    ? 'fa fa-minus-circle icn-yellow m-2'
                                    : 'fa fa-plus-circle icn-black m-2'
                                }
                                aria-hidden="true"
                              />
                            )}
                            {!row.isHeader && row.account_chart.account ? (
                              <span className="bva-acct">
                                {row.account_chart.account}
                              </span>
                            ) : null}

                            {row.account_chart.name}
                          </td>

                          {row.isHeader ? (
                            <td colSpan={colSpanCount || 4} />
                          ) : (
                            row.months.map((m, mi) => (
                              <React.Fragment key={mi}>
                                <td
                                  className={`bva-month-start ${
                                    m.month_label === 'Total'
                                      ? 'bva-total-col'
                                      : ''
                                  }`}
                                >
                                  {formatAmount(m.actual)}
                                </td>

                                <td
                                  className={
                                    m.month_label === 'Total'
                                      ? 'bva-total-col'
                                      : ''
                                  }
                                >
                                  {formatAmount(m.budget)}
                                </td>

                                <td
                                  className={`${varianceClass(m.over_budget)} ${
                                    m.month_label === 'Total'
                                      ? 'bva-total-col'
                                      : ''
                                  }`}
                                >
                                  {formatAmount(m.over_budget)}
                                </td>

                                <td
                                  className={
                                    m.month_label === 'Total'
                                      ? 'bva-total-col'
                                      : ''
                                  }
                                >
                                  {renderPct(m.pct_of_budget)}
                                </td>
                              </React.Fragment>
                            ))
                          )}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                <ScrollToggle scrollRef={tableContainerRef} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default BudgetVsActual
